import React, { useState, useEffect, useCallback } from 'react';
import Navbar from "../components/Navbar";
import './fashion.css';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import { useSwipeable } from 'react-swipeable';


function ImageModal({ image, onClose, images, currentIndex, setSelectedImage }) {

  const goNext = useCallback(() => {
    const nextIndex = (currentIndex + 1) % images.length;
    setSelectedImage(images[nextIndex]);
  }, [currentIndex, images, setSelectedImage]);

  const goPrev = useCallback(() => {
    const prevIndex = (currentIndex - 1 + images.length) % images.length;
    setSelectedImage(images[prevIndex]);
  }, [currentIndex, images, setSelectedImage]);

  const handlers = useSwipeable({
    onSwipedLeft: goNext,
    onSwipedRight: goPrev,
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  });

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowRight") {
        goNext();
      } else if (e.key === "ArrowLeft") {
        goPrev();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, goNext, goPrev]);

  if (!image) return null;

  return (
    <div className="modal-overlay" onClick={onClose} {...handlers}>
      {/* Arrows */}
      <button className="fashion_modal-arrow left" onClick={(e) => { e.stopPropagation(); goPrev(); }}>‹</button>
      <button className="fashion_modal-arrow right" onClick={(e) => { e.stopPropagation(); goNext(); }}>›</button>

      <div className="modal-content">
        <div className="modal-inner">
          <img
            src={image.src}
            alt={image.alt}
            className="modal-image"
            onClick={(e) => e.stopPropagation()}
          />
          {image.credit && (
            <div className="fashion_credit-text">
              {image.credit.split('\n').map((line, idx) => (
                <p key={idx}>{line}</p>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



const Fashion = () => {

  const [selectedImage, setSelectedImage] = useState(null);
  const [modalImages, setModalImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [info_open, setInfo_open] = useState(null);

  // More info descriptions

  const info_descriptions = {
    nocturne: {
      title: 'Nocturne',
      body: 'In 7th grade, I created my first dress. It was piano-themed and made for a recital I would be performing at. Nocturne is my second recreation of that original piano dress. It features a hand-detailed treble clef that merges into the halter of the dress, and a full-length skirt. The sleeves and drapery in the skirt are hand-beaded with pearls and glass bugle beads. I chose box pleats for the skirt because the long rectangular shapes resemble the black keys of a piano.',
    },
    lescrimeuse: {
      title: "L'escrimeuse",
      body: 'Famous depictions of women’s fencing featured a white bodice, black skirt, and a signature red heart on the left breast. A painting by Jean Béraud, titled “L’escrimeuse,” was one of these depictions. I aimed to capture the core historical elements of this outfit in my design, while also adding a modern twist.\n\nI kept the iconic white bodice and signature red heart, adorned with red rhinestones so it would sparkle under lights. Black sleeves became classy black gloves, where I used interfacing and wire to create their gravity-defying shape. The skirt of the white dress is tapered to a V-shape to represent the silhouette of modern fencing jackets. The white ruffled collar symbolizes the high neckline of both historical and modern fencing attire, and the golden chains tie all the elements together. The long train, inspired by the black skirts of Victorian fencers, is two-toned with red fabric on the inside, adding a pop of color that complements the bodice’s red heart, and is accurate to the painting by Jean Béraud. Lastly, my choice of sword was an épée for its recognizable style and its applications in modern fencing.',
    },
    miss2023: {
      title: 'Miss 2023',
      body: 'A dress to symbolize my departure from high school. Class of 2023.\n\nIn senior year, it’s customary for girls to wear a drape for their yearbook photo. This dress was inspired by the silhouette of that drape, its off-shoulder, V-shaped design, and velvet fabric. Instead of black, I chose dark purple because it was the color I wore to my graduation ceremony. The skirt of Miss 2023 was my own creative interpretation of what the graduation drape would look like if it were a real dress. I enjoyed the idea of an asymmetrical outer skirt layer and an inner layer being the drape’s traditional black. I chose a patterned velvet for the inner skirt layer to add interest to the design while also matching the purple velvet outer layer.',
    },
    danalia: {
      title: 'The Danalia Gown',
      body: 'At the beginning of high school, I sketched a gown for one of my original characters, Danalia. The drawings were only intended to be for fun, and I never imagined one day making the dress in real life, let alone for senior prom.\n\nThe Danalia gown features an ivory underskirt with a light green overskirt layer, both consisting of numerous panels, meticulously pinned and sewn to create ruffles and drapes around the dress. White roses, pearls, and gold chains were also hand-sewn to the skirt.\n\nThe bodice consists of a light green base layer and a caramel-colored corset on top. The corset was carefully patterned to house boning channels and eyelets for a lace-up back. The front of both bodice pieces were also hand-detailed with pearls and chain.\n\nIn addition to the dress, I created accessories to match. The necklace and earrings were handmade, along with the white rose hairpins created with faux roses and black wire.',
    },
    nocturne_v1: {
      title: 'Nocturne (Version 1)',
      body: 'This dress is a tribute to the first dress I ever made: a piano-themed garment I created in 7th grade.\n\nThis new dress features an off-shoulder keyboard pattern design, as it was a staple of the first dress. The dress is predominantly black, representing a traditional grand piano. Black also adds a classier aesthetic and contrasts with the original white dress. I made the skirt shorter and more fitted to further establish the evolution from the original design to the new one.',
    },
    ambrosia: {
      title: 'Ambrosia',
      body: 'This dress was one of my more technically challenging dresses at the time, featuring a 720° double circle skirt lined with horsehair braid, and a heavily boned bodice with a corset-laced back. Though, as a result, this dress pushed me to further develop my skills as a seamstress.',
    },
    penelope: {
      title: 'Penelope',
      body: 'I found inspiration upon staring at this pink sherpa fabric, a material used commonly in jackets, sweaters, and blankets, but not in dresses. But first, I made an octopus plushie because I simply could not resist. And then, I decided to create a garment to go with it. I thought about the basic qualities of my fluffy, round plushie, who I named Penelope, and tried to figure out how to translate them into dress form. From the cupcake-shaped skirt to the puffy polka dot-patterned sleeves, the flowery lace trim, and the ruffled peplum, I aimed to embody the word “soft.” Soft colors, edges, and fabric were paramount to achieving my vision, which was basically to create a life-sized interpretation of Penelope.',
    },
  };


  const nocturneImages = [
    { src: '/images/fashionCarousel/nocturne/nocturne_1.jpg', alt: 'Nocturne 1', credit: 'Model: Chanel Zong\nPhotographer: Emma (@digitalpix.ie)' },
    { src: '/images/fashionCarousel/nocturne/nocturne_2.jpg', alt: 'Nocturne 2', credit: 'Model: Chanel Zong\nPhotographer: Emma (@digitalpix.ie)' },
    { src: '/images/fashionCarousel/nocturne/nocturne_3.jpg', alt: 'Nocturne 3', credit: 'Model: Chanel Zong\nPhotographer: Nathan (@nthnschen)' },
    { src: '/images/fashionCarousel/nocturne/nocturne_4.jpg', alt: 'Nocturne 4', credit: 'Model: Chanel Zong\nPhotographer: Mia Liu' },
    { src: '/images/fashionCarousel/nocturne/nocturne_5.jpg', alt: 'Nocturne 5', credit: 'Model: Chanel Zong\nPhotographer: Mia Liu' },
    { src: '/images/fashionCarousel/nocturne/nocturne_6.jpg', alt: 'Nocturne 6', credit: 'Model: Chanel Zong\nPhotographer: Mia Liu' },
    { src: '/images/fashionCarousel/nocturne/nocturne_7.jpg', alt: 'Nocturne 7', credit: 'Model: Chanel Zong\nPhotographer: Mia Liu' },
    { src: '/images/fashionCarousel/nocturne/nocturne_8.jpg', alt: 'Nocturne 8', credit: 'Model: Chanel Zong\nPhotographer: Oura (@omiyaz)' },
    { src: '/images/fashionCarousel/nocturne/nocturne_9.jpg', alt: 'Nocturne 9' },
    { src: '/images/fashionCarousel/nocturne/nocturne_10.jpg', alt: 'Nocturne 10', credit: '' },
    { src: '/images/fashionCarousel/nocturne/nocturne_11.jpg', alt: 'Nocturne 11', credit: '' },
    { src: '/images/fashionCarousel/nocturne/nocturne_12.jpg', alt: 'Nocturne 12', credit: '' },
  ];

  const lescrimeuseImages = [
    { src: '/images/fashionCarousel/lescrimeuse/lescrimeuse_1.jpg', alt: 'Lescrimeuse 1', credit: 'Model: Mia Liu\nPhotographer: Emma (@digitalpix.ie)' },
    { src: '/images/fashionCarousel/lescrimeuse/lescrimeuse_2.jpg', alt: 'Lescrimeuse 2', credit: 'Model: Mia Liu\nPhotographer: Nathan (@nthnschen)' },
    { src: '/images/fashionCarousel/lescrimeuse/lescrimeuse_3.jpg', alt: 'Lescrimeuse 3', credit: 'Model: Mia Liu\nPhotographer: Nathan (@nthnschen)' },
    { src: '/images/fashionCarousel/lescrimeuse/lescrimeuse_4.jpg', alt: 'Lescrimeuse 4', credit: 'Model: Mia Liu\nPhotographer: Nathan (@nthnschen)' },
    { src: '/images/fashionCarousel/lescrimeuse/lescrimeuse_5.jpg', alt: 'Lescrimeuse 5', credit: 'Model: Mia Liu\nPhotographer: Emma (@digitalpix.ie)' },
    { src: '/images/fashionCarousel/lescrimeuse/lescrimeuse_6.jpg', alt: 'Lescrimeuse 6', credit: 'Model: Mia Liu\nPhotographer: Emma (@digitalpix.ie)' },
    { src: '/images/fashionCarousel/lescrimeuse/lescrimeuse_7.jpg', alt: 'Lescrimeuse 7', credit: 'Model: Mia Liu\nPhotographer: Oura (@omiyaz)' },
    // { src: '/images/fashionCarousel/lescrimeuse/lescrimeuse_8.jpg', alt: 'Lescrimeuse 8' },
    { src: '/images/fashionCarousel/lescrimeuse/lescrimeuse_9.jpg', alt: 'Lescrimeuse 9' },
    { src: '/images/fashionCarousel/lescrimeuse/lescrimeuse_10.jpg', alt: 'Lescrimeuse 10' },
    { src: '/images/fashionCarousel/lescrimeuse/lescrimeuse_11.jpg', alt: 'Lescrimeuse 11' },
    { src: '/images/fashionCarousel/lescrimeuse/lescrimeuse_12.jpg', alt: 'Lescrimeuse 12' },
  ];

  const miss2023Images = [
    { src: '/images/fashionCarousel/miss_2023/miss1.jpg', alt: 'Miss 2023 1', credit: 'Model: Lily Philips\nPhotographer: Emma (@digitalpix.ie)' },
    // { src: '/images/fashionCarousel/miss_2023/miss_2023_2.jpg', alt: 'Miss 2023 2', credit: 'Model: Lily Philips\nPhotographer: Emma (@digitalpix.ie)' },
    { src: '/images/fashionCarousel/miss_2023/miss3.jpg', alt: 'Miss 2023 3', credit: 'Model: Lily Philips\nPhotographer: Emma (@digitalpix.ie)' },
    { src: '/images/fashionCarousel/miss_2023/miss4.jpg', alt: 'Miss 2023 4', credit: 'Model: Lily Philips\nPhotographer: Emma (@digitalpix.ie)' },
    { src: '/images/fashionCarousel/miss_2023/miss5.jpg', alt: 'Miss 2023 5', credit: 'Model: Lily Philips\nPhotographer: Emma (@digitalpix.ie)' },
    { src: '/images/fashionCarousel/miss_2023/miss_2023_6.jpg', alt: 'Miss 2023 6', credit: 'Model: Lily Philips\nPhotographer: Oura (@omiyaz)' },
    { src: '/images/fashionCarousel/miss_2023/miss_2023_7.jpg', alt: 'Miss 2023 7', credit: 'Model: Lily Philips\nPhotographer: Nathan (@nthnschen)' },
    { src: '/images/fashionCarousel/miss_2023/miss_2023_8.jpg', alt: 'Miss 2023 8', credit: 'Model: Lily Philips\nPhotographer: Nathan (@nthnschen)' },
    { src: '/images/fashionCarousel/miss_2023/miss_2023_9.jpg', alt: 'Miss 2023 9', credit: 'Model: Lily Philips\nPhotographer: Mia Liu' },
    { src: '/images/fashionCarousel/miss_2023/miss_2023_10.jpg', alt: 'Miss 2023 10'},
    { src: '/images/fashionCarousel/miss_2023/miss_2023_11.jpg', alt: 'Miss 2023 11' },
    { src: '/images/fashionCarousel/miss_2023/miss_2023_12.jpg', alt: 'Miss 2023 12' },
  ];

  const danaliaImages = [
    { src: '/images/fashionCarousel/danalia/danalia1.jpg', alt: 'Danalia 1', },
    { src: '/images/fashionCarousel/danalia/danalia9.jpg', alt: 'Danalia 9', },
    { src: '/images/fashionCarousel/danalia/danalia3.jpg', alt: 'Danalia 3'},
    { src: '/images/fashionCarousel/danalia/danalia4.jpg', alt: 'Danalia 4' },
    { src: '/images/fashionCarousel/danalia/danalia5.jpg', alt: 'Danalia 5' },
    { src: '/images/fashionCarousel/danalia/danalia6.jpg', alt: 'Danalia 6' },
    { src: '/images/fashionCarousel/danalia/danalia7.jpg', alt: 'Danalia 7' },
    { src: '/images/fashionCarousel/danalia/danalia8.jpg', alt: 'Danalia 8' },
    { src: '/images/fashionCarousel/danalia/danalia10.jpg', alt: 'Danalia 10' },
    { src: '/images/fashionCarousel/danalia/danalia11.jpg', alt: 'Danalia 11', credit: 'Necklace (also handmade)' },
  ]

  const nocturne_v1Images = [
    { src: '/images/fashionCarousel/nocturne_v1/nocturne_v1_1.jpg', alt: 'Nocturne V1 1' },
    { src: '/images/fashionCarousel/nocturne_v1/nocturne_v1_2.jpg', alt: 'Nocturne V1 2'},
    { src: '/images/fashionCarousel/nocturne_v1/nocturne_v1_3.jpg', alt: 'Nocturne V1 3' },
    { src: '/images/fashionCarousel/nocturne_v1/nocturne_v1_4.jpg', alt: 'Nocturne V1 3' },
  ]

  const ambrosiaImages = [
    { src: '/images/fashionCarousel/ambrosia/ambrosia1.jpg', alt: 'Ambrosia 1' },
    { src: '/images/fashionCarousel/ambrosia/ambrosia2.jpg', alt: 'Ambrosia 2' },
    { src: '/images/fashionCarousel/ambrosia/ambrosia3.jpg', alt: 'Ambrosia 3' },
    { src: '/images/fashionCarousel/ambrosia/ambrosia4.jpg', alt: 'Ambrosia 4'},
    { src: '/images/fashionCarousel/ambrosia/ambrosia5.jpg', alt: 'Ambrosia 5' },
    { src: '/images/fashionCarousel/ambrosia/ambrosia6.jpg', alt: 'Ambrosia 6' },
  ]

  const penelopeImages = [
    { src: '/images/fashionCarousel/penelope/penelope1.jpg', alt: 'Penelope 1' },
    { src: '/images/fashionCarousel/penelope/penelope2.jpg', alt: 'Penelope 2' },
    { src: '/images/fashionCarousel/penelope/penelope3.jpg', alt: 'Penelope 3' },
    { src: '/images/fashionCarousel/penelope/penelope4.jpg', alt: 'Penelope 4' },
    { src: '/images/fashionCarousel/penelope/penelope5.jpg', alt: 'Penelope 5' },
    { src: '/images/fashionCarousel/penelope/penelope6.jpg', alt: 'Penelope 6' },
  ]

  const otherImages = [
    { src: '/images/fashionCarousel/other/blue.jpg', alt: 'Blue', credit: "Blue" },
    { src: '/images/fashionCarousel/other/homage.jpg', alt: 'Homage', credit: "Homage" },
    { src: '/images/fashionCarousel/other/whitePuff.jpg', alt: 'White Puff', credit: "White Puff" },
    { src: '/images/fashionCarousel/other/theaterOfRoses.jpg', alt: 'Theater of Roses', credit: "Theater of Roses" },
    { src: '/images/fashionCarousel/other/serpent.jpg', alt: 'Serpent', credit: "Serpent" },
    { src: '/images/fashionCarousel/other/byTheLightOfTheMoon.jpg', alt: 'By the Light of the Moon', credit: "By the Light of the Moon" },
    { src: '/images/fashionCarousel/other/vintageRose.jpg', alt: 'Vintage Rose', credit: "Vintage Rose" },
    { src: '/images/fashionCarousel/other/maia.jpg', alt: 'Maia', credit: "Maia" },
    
  ]

  const responsive = {
    superLargeDesktop: {
      breakpoint: { max: 4000, min: 3000 },
      items: 6,
    },
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 4,
    },
    tablet: {
      breakpoint: { max: 1024, min: 464 },
      items: 3,
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: 1,
    },
  };

  return (
    <div>
      <Navbar />
      <h1 className="header" style={{ fontFamily: 'Cormorant SC, serif', fontSize: '3.5rem' }}>Fashion</h1>

      {/* Nocturne */}
      <hr className="line" />
      <div className="collectionTitle" style={{ padding: '20px' }}>
        <div className="collectionTitleHeader">
          <h1>Nocturne</h1>
          <button className="infoButton" onClick={() => setInfo_open('nocturne')}>more about this design</button>
        </div>
        <Carousel 
          responsive={responsive} 
          infinite={true} 
          autoPlay={true} 
          swipeable={true}
          itemClass="fashion_carousel-item"
          containerClass="carousel"
        >
          {nocturneImages.map((image, index) => (
            <div key={index}>
              <img
                src={image.src}
                alt={image.alt}
                className="fashion_carousel-image"
                onClick={() => {
                  setSelectedImage(image);
                  setModalImages(nocturneImages);
                  setCurrentIndex(index);
                }}
                style={{ cursor: 'pointer' }}
              />
            </div>
          ))}

        </Carousel>
      </div>

      {/* L'escrimeuse */}

      <hr className="line" />
      <div className="collectionTitle" style={{ padding: '20px' }}>
        <div className="collectionTitleHeader">
          <h1>L'escrimeuse</h1>
          <button className="infoButton" onClick={() => setInfo_open('lescrimeuse')}>more about this design</button>
        </div>
        <Carousel 
          responsive={responsive} 
          infinite={true} 
          autoPlay={true} 
          swipeable={true}
          itemClass="fashion_carousel-item"
          containerClass="carousel"
        >
          {lescrimeuseImages.map((image, index) => (
            <div key={index}>
              <img
                src={image.src}
                alt={image.alt}
                className="fashion_carousel-image"
                onClick={() => {
                  setSelectedImage(image);
                  setModalImages(lescrimeuseImages);
                  setCurrentIndex(index);
                }}
                style={{ cursor: 'pointer' }}
              />
            </div>
          ))}
        </Carousel>
      </div>

      {/* Miss 2023 */}

      <hr className="line" />
      <div className="collectionTitle" style={{ padding: '20px' }}>
        <div className="collectionTitleHeader">
          <h1>Miss 2023</h1>
          <button className="infoButton" onClick={() => setInfo_open('miss2023')}>more about this design</button>
        </div>
        <Carousel 
          responsive={responsive} 
          infinite={true} 
          autoPlay={true} 
          swipeable={true}
          itemClass="fashion_carousel-item"
          containerClass="carousel"
        >
          {miss2023Images.map((image, index) => (
            <div key={index}>
              <img
                src={image.src}
                alt={image.alt}
                className="fashion_carousel-image"
                onClick={() => {
                  setSelectedImage(image);
                  setModalImages(miss2023Images);
                  setCurrentIndex(index);
                }}
                style={{ cursor: 'pointer' }}
              />
            </div>
          ))}
        </Carousel>
      </div>


      {/* Danalia */}

      <hr className="line" />
      <div className="collectionTitle" style={{ padding: '20px' }}>
        <div className="collectionTitleHeader">
          <h1>The Danalia Gown</h1>
          <button className="infoButton" onClick={() => setInfo_open('danalia')}>more about this design</button>
        </div>
        <Carousel 
          responsive={responsive} 
          infinite={true} 
          autoPlay={true} 
          swipeable={true}
          itemClass="fashion_carousel-item"
          containerClass="carousel"
        >
          {danaliaImages.map((image, index) => (
            <div key={index}>
              <img
                src={image.src}
                alt={image.alt}
                className="fashion_carousel-image"
                onClick={() => {
                  setSelectedImage(image);
                  setModalImages(danaliaImages);
                  setCurrentIndex(index);
                }}
                style={{ cursor: 'pointer' }}
              />
            </div>
          ))}
        </Carousel>
      </div>


      {/* Nocturne V1 */}

      <hr className="line" />
      <div className="collectionTitle" style={{ padding: '20px' }}>
        <div className="collectionTitleHeader">
          <h1>Nocturne (Version 1)</h1>
          <button className="infoButton" onClick={() => setInfo_open('nocturne_v1')}>more about this design</button>
        </div>
        <Carousel 
          responsive={responsive} 
          infinite={true} 
          autoPlay={true} 
          swipeable={true}
          itemClass="fashion_carousel-item"
          containerClass="carousel"
        >
          {nocturne_v1Images.map((image, index) => (
            <div key={index}>
              <img
                src={image.src}
                alt={image.alt}
                className="fashion_carousel-image"
                onClick={() => {
                  setSelectedImage(image);
                  setModalImages(nocturne_v1Images);
                  setCurrentIndex(index);
                }}
                style={{ cursor: 'pointer' }}
              />
            </div>
          ))}
        </Carousel>
      </div>


      {/* Ambrosia */}

      <hr className="line" />
      <div className="collectionTitle" style={{ padding: '20px' }}>
        <div className="collectionTitleHeader">
          <h1>Ambrosia</h1>
          <button className="infoButton" onClick={() => setInfo_open('ambrosia')}>more about this design</button>
        </div>
        <Carousel 
          responsive={responsive} 
          infinite={true} 
          autoPlay={true} 
          swipeable={true}
          itemClass="fashion_carousel-item"
          containerClass="carousel"
        >
          {ambrosiaImages.map((image, index) => (
            <div key={index}>
              <img
                src={image.src}
                alt={image.alt}
                className="fashion_carousel-image"
                onClick={() => {
                  setSelectedImage(image);
                  setModalImages(ambrosiaImages);
                  setCurrentIndex(index);
                }}
                style={{ cursor: 'pointer' }}
              />
            </div>
          ))}
        </Carousel>
      </div>


      {/* Penelope */}

      <hr className="line" />
      <div className="collectionTitle" style={{ padding: '20px' }}>
        <div className="collectionTitleHeader">
          <h1>Penelope</h1>
          <button className="infoButton" onClick={() => setInfo_open('penelope')}>more about this design</button>
        </div>
        <Carousel 
          responsive={responsive} 
          infinite={true} 
          autoPlay={true} 
          swipeable={true}
          itemClass="fashion_carousel-item"
          containerClass="carousel"
        >
          {penelopeImages.map((image, index) => (
            <div key={index}>
              <img
                src={image.src}
                alt={image.alt}
                className="fashion_carousel-image"
                onClick={() => {
                  setSelectedImage(image);
                  setModalImages(penelopeImages);
                  setCurrentIndex(index);
                }}
                style={{ cursor: 'pointer' }}
              />
            </div>
          ))}
        </Carousel>
      </div>


      {/* Other Designs */}

      <hr className="line" />
      <div className="collectionTitle" style={{ padding: '20px' }}>
        <h1>(Other Designs)</h1>
        <Carousel 
          responsive={responsive} 
          infinite={true} 
          autoPlay={true} 
          swipeable={true}
          itemClass="fashion_carousel-item"
          containerClass="carousel"
        >
          {otherImages.map((image, index) => (
            <div key={index}>
              <img
                src={image.src}
                alt={image.alt}
                className="fashion_carousel-image"
                onClick={() => {
                  setSelectedImage(image);
                  setModalImages(otherImages);
                  setCurrentIndex(index);
                }}
                style={{ cursor: 'pointer' }}
              />
            </div>
          ))}
        </Carousel>
      </div>

      {/* Footer */}

      <div className="footer__container">
        <div className="websiteRights">
          <p>© MINYA 2025 | all rights reserved</p>
        </div>
      </div>

      <div className={`info-overlay ${info_open ? 'open' : ''}`} onClick={() => setInfo_open(null)}>
        {info_open && (
          <div className="info-content">
            <div
              className="info-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-label={`${info_descriptions[info_open]?.title || 'Info'} dialog`}
            >
              <h2>{info_descriptions[info_open]?.title}</h2>
              <p>{info_descriptions[info_open]?.body}</p>
              <button className="closeButton" onClick={() => setInfo_open(null)}>x</button>
            </div>
          </div>
        )}
      </div>


      <ImageModal
        image={selectedImage}
        images={modalImages}
        currentIndex={currentIndex}
        setSelectedImage={(img) => {
          setSelectedImage(img);
          setCurrentIndex(modalImages.findIndex(i => i.src === img.src));
        }}
        onClose={() => {
          setSelectedImage(null);
          setModalImages([]);
          setCurrentIndex(0);
        }}
      />

    </div>
  );
};

export default Fashion;
